import type {
  CreateTraitDefinitionInput,
  TraitDefinitionsBoolExp,
  TraitDefinitionsInsertInput,
  TraitDefinitionsOrderBy,
  TraitDefinitionsSetInput,
  TraitDefinitionsSortDirection,
  TraitDefinitionsSortKey,
  TraitsQueryVariables,
  UpdateTraitDefinitionInput,
} from "src/graphql";
import { OrderBy } from "src/graphql";

/**
 * Drops `__typename` at every level. The legacy and new-path selections ask for
 * the same fields, but their rows come from different GraphQL types
 * (`trait_definitions` vs `TraitDefinition`, and likewise for nested trait
 * types), so `__typename` is the one field that legitimately differs.
 */
export type StripTypenames<T> = T extends (infer TElement)[]
  ? Array<StripTypenames<TElement>>
  : T extends object
    ? { [K in keyof T as Exclude<K, "__typename">]: StripTypenames<T[K]> }
    : T;

/**
 * Presents a legacy (Hasura `trait_definitions`) row as its new-path
 * (`TraitDefinition`) twin. The parameter type admits every difference except
 * `__typename`, so any *other* divergence between the two selections is a
 * compile error here instead of a runtime surprise downstream.
 *
 * Deleted along with the *Legacy operations once appUseNewTraitsEndpoints is
 * retired.
 */
export const asNewShape = <TNew>(legacy: StripTypenames<TNew>): TNew =>
  legacy as TNew;

export const asNewShapes = <TNew>(
  legacy: Array<StripTypenames<TNew>>,
): TNew[] => legacy as TNew[];

/**
 * Translates the `Traits` arguments into the `trait_definitions_bool_exp` the
 * legacy query takes. The `_or` search reproduces the pre-migration traits-page
 * filter (name / model name / related model name / type), which the resolver
 * implements in SQL.
 */
export const toLegacyTraitsFilter = ({
  segmentId,
  isTemplate,
  archived,
  search,
}: Pick<
  TraitsQueryVariables,
  "segmentId" | "isTemplate" | "archived" | "search"
>): TraitDefinitionsBoolExp => {
  const filter: TraitDefinitionsBoolExp = {};

  if (search) {
    filter._or = [
      { name: { _ilike: `%${search}%` } },
      { segment: { name: { _ilike: `%${search}%` } } },
      { relationship: { to_model: { name: { _ilike: `%${search}%` } } } },
      { type: { _ilike: `%${search}%` } },
    ];
  }
  if (segmentId != null) {
    filter.segment_id = { _eq: segmentId };
  }
  if (isTemplate != null) {
    filter.is_template = { _eq: isTemplate };
  }
  if (archived != null) {
    filter.archived_at = { _is_null: !archived };
  }

  return filter;
};

/**
 * Builds the legacy `order_by` for a sort key, reproducing what the traits page
 * sent before the migration. `relatedModelName` sorts on the same expression the
 * resolver uses; `activeTraitCount` can't — Hasura's aggregate `order_by` takes
 * no filter, so it counts every child trait, archived ones included, where the
 * resolver counts only the non-archived children the page actually displays.
 * That disagreement between the sort and the visible count is the pre-flag
 * behavior, and it goes away with this path.
 */
export const toLegacyTraitsOrderBy = (
  sortKey: TraitDefinitionsSortKey | null | undefined,
  sortDirection: TraitDefinitionsSortDirection | null | undefined,
): TraitDefinitionsOrderBy[] => {
  const direction = sortDirection === "asc" ? OrderBy.Asc : OrderBy.Desc;

  switch (sortKey) {
    case "name":
      return [{ name: direction }];
    case "type":
      return [{ type: direction }];
    case "createdAt":
      return [{ created_at: direction }];
    case "updatedAt":
      return [{ updated_at: direction }];
    case "relatedModelName":
      return [{ relationship: { to_model: { name: direction } } }];
    case "activeTraitCount":
      return [{ child_traits_aggregate: { count: direction } }];
    default:
      return [{ created_at: OrderBy.Desc }];
  }
};

/**
 * Column names for each input field, mirroring the resolvers' own
 * input-to-column tables. The `satisfies` clauses fail the build if a field is
 * added to either input without a column here.
 */
const CREATE_INPUT_COLUMNS = {
  name: "name",
  type: "type",
  config: "config",
  segmentId: "segment_id",
  relationshipId: "relationship_id",
  description: "description",
  isTemplate: "is_template",
  predictorId: "predictor_id",
  traitTemplateId: "trait_template_id",
  topKEnabled: "top_k_enabled",
  topKSyncInterval: "top_k_sync_interval",
  triggerTopKSync: "trigger_top_k_sync",
} satisfies Record<
  keyof CreateTraitDefinitionInput,
  keyof TraitDefinitionsInsertInput
>;

const UPDATE_INPUT_COLUMNS = {
  name: "name",
  description: "description",
  type: "type",
  config: "config",
  relationshipId: "relationship_id",
  predictorId: "predictor_id",
  archivedAt: "archived_at",
  topKEnabled: "top_k_enabled",
  topKSyncInterval: "top_k_sync_interval",
  triggerTopKSync: "trigger_top_k_sync",
} satisfies Record<
  keyof UpdateTraitDefinitionInput,
  keyof TraitDefinitionsSetInput
>;

const toColumns = <TInput extends object, TOutput>(
  input: TInput,
  columns: Record<keyof TInput & string, string>,
): TOutput => {
  const result: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(input)) {
    // Omitted fields must stay omitted: both paths treat `undefined` as "leave
    // alone" and an explicit null as "write null".
    if (value !== undefined) {
      result[columns[field as keyof TInput & string]] = value;
    }
  }
  return result as TOutput;
};

export const toLegacyCreateInput = (
  input: CreateTraitDefinitionInput,
): TraitDefinitionsInsertInput =>
  toColumns<CreateTraitDefinitionInput, TraitDefinitionsInsertInput>(
    input,
    CREATE_INPUT_COLUMNS,
  );

export const toLegacyUpdateInput = (
  input: UpdateTraitDefinitionInput,
): TraitDefinitionsSetInput =>
  toColumns<UpdateTraitDefinitionInput, TraitDefinitionsSetInput>(
    input,
    UPDATE_INPUT_COLUMNS,
  );
